import moment from 'moment'

// "Re-checked maximum seniority" — a position's max seniority blocks candidates
// in the chatbot only once someone explicitly re-checked (stamped) the SAVED
// number. Everything about the back-end contract lives here: the four
// read-only serializer fields, the stamp route, the response vocabulary and
// the Hebrew copy. Pure functions only — no Vue, no network.

export const MAX_SENIORITY_CHECKS_PATH = '/positions/max_seniority_checks'

export const CHECK_FIELDS = {
  at: 'max_seniority_checked_at',
  by: 'max_seniority_checked_by',
  value: 'max_seniority_checked_value',
  kind: 'max_seniority_checked_kind'
}

export const CHECK_KINDS = { recruiter: 'recruiter', ai_review: 'ai_review' }

export const STAMP_STATUS = { stamped: 'stamped', skipped: 'skipped' }
export const STAMP_REASONS = {
  not_found: 'not_found',
  max_changed: 'max_changed',
  no_max: 'no_max',
  // the position row was busy — transient, the recruiter simply retries
  locked: 'locked',
  // only returned to non-UI callers; handled defensively by re-reading the stamp
  already_checked: 'already_checked'
}
// HTTP 400 reasons for a malformed request body — the UI never builds one, so
// they get the generic error.
export const BAD_REQUEST_REASONS = ['checks_required', 'too_many_checks', 'invalid_item']

// 1 -> 'שנה אחת', otherwise '<n> שנים'
export const yearsText = n => (Number(n) === 1 ? 'שנה אחת' : `${n} שנים`)

export const TEXTS = {
  recheckedPrefix: 'נבדק מחדש',
  aiTag: 'בדיקת AI',
  notRechecked: 'לא נבדק מחדש – המערכת מתייחסת למקסימום כלא מאומת',
  button: 'אישור מחדש של המקסימום',
  saveFirst: 'יש לשמור את המקסימום לפני האישור',
  stamped: 'המקסימום אושר מחדש',
  // the editor has already re-read the position when this is shown
  maxChanged: current => `המקסימום השתנה בינתיים (${current == null ? 'ללא' : yearsText(current)}) והערך העדכני נטען. בדקי ואשרי שוב`,
  noMax: 'לא נשמר מקסימום ותק למשרה, ולכן אין מה לאשר. יש להזין מקסימום, לשמור ולנסות שוב',
  locked: 'המשרה בעדכון כרגע. נסי שוב בעוד רגע',
  notFound: 'המשרה לא נמצאה בשרת (ייתכן שנמחקה). סגרי את החלון ופתחי את המשרה מחדש',
  unauthorized: 'האישור נכשל: אין הרשאה או שפג תוקף ההתחברות. התחברי מחדש ונסי שוב',
  badRequest: 'האישור נכשל: השרת דחה את הבקשה. רענני את המשרה ונסי שוב',
  notReady: 'האישור עדיין לא זמין בשרת. נסי שוב מאוחר יותר',
  generic: 'האישור נכשל בגלל תקלה. נסי שוב בעוד רגע'
}

// '' / null / undefined -> null (no maximum); a finite number (or numeric
// string) -> that number; anything else -> NaN (an invalid typed value).
export const toMax = value => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim() === '') return null
  const n = Number(value)
  return isFinite(n) ? n : NaN
}

// Numeric compare; two "no maximum"s are equal, an invalid value equals nothing.
export const sameMax = (a, b) => {
  const x = toMax(a)
  const y = toMax(b)
  if (x === null || y === null) return x === y
  return x === y // NaN !== NaN
}

// The four fields out of a position / save response / stamp result.
// null when the object carries none of them — an older server: show nothing.
export const pickMaxSeniorityCheck = source => {
  if (!source || typeof source !== 'object') return null
  const keys = Object.keys(CHECK_FIELDS).map(k => CHECK_FIELDS[k])
  if (!keys.some(key => key in source)) return null
  return {
    checked_at: source[CHECK_FIELDS.at] || null,
    checked_by: source[CHECK_FIELDS.by] || null,
    checked_value: source[CHECK_FIELDS.value] === undefined ? null : source[CHECK_FIELDS.value],
    checked_kind: source[CHECK_FIELDS.kind] || null
  }
}

// Same shape from one entry of the stamp response (`checked_at`, ...).
export const checkFromStampResult = result => ({
  checked_at: result.checked_at || null,
  checked_by: result.checked_by || null,
  checked_value: result.max_seniority === undefined ? null : result.max_seniority,
  checked_kind: result.checked_kind || null
})

// Re-checked ONLY when stamped AND the stamped value is the saved maximum.
export const isMaxSeniorityRechecked = (check, savedMax) => {
  if (!check || !check.checked_at) return false
  if (toMax(savedMax) === null) return false
  return sameMax(check.checked_value, savedMax)
}

// { variant: 'hidden' | 'recruiter' | 'ai_review' | 'not_rechecked', text }
export const buildMaxSeniorityStatus = (check, savedMax) => {
  if (!check) return { variant: 'hidden', text: '' }
  const max = toMax(savedMax)
  if (max === null || isNaN(max)) return { variant: 'hidden', text: '' }
  if (!isMaxSeniorityRechecked(check, savedMax)) return { variant: 'not_rechecked', text: TEXTS.notRechecked }
  const isAi = check.checked_kind === CHECK_KINDS.ai_review
  const parts = [yearsText(max)]
  const date = moment(check.checked_at)
  if (date.isValid()) parts.push(date.format('DD/MM/YYYY'))
  const name = typeof check.checked_by === 'string' ? check.checked_by.trim() : ''
  if (name) parts.push(name)
  const prefix = isAi ? `${TEXTS.recheckedPrefix} (${TEXTS.aiTag})` : TEXTS.recheckedPrefix
  const detail = parts.join(' · ')
  // label / tag / detail let the template render the AI part as a small tag
  // while the full text stays exactly `text`.
  return {
    variant: isAi ? 'ai_review' : 'recruiter',
    text: `${prefix}: ${detail}`,
    label: TEXTS.recheckedPrefix,
    tag: isAi ? TEXTS.aiTag : '',
    detail
  }
}

// Button rules. `visible`: a maximum is saved and it is not re-checked.
// `disabled` + `hint`: the input holds an unsaved value (hint), or a request
// (stamp / max save) is running.
export const maxSeniorityConfirmState = ({ check, savedMax, inputValue, busy }) => {
  const max = toMax(savedMax)
  const visible = !!check && max !== null && !isNaN(max) && !isMaxSeniorityRechecked(check, savedMax)
  if (!visible) return { visible: false, disabled: true, hint: '' }
  const unsaved = !sameMax(inputValue, savedMax)
  return { visible: true, disabled: unsaved || !!busy, hint: unsaved ? TEXTS.saveFirst : '' }
}

export const buildStampRequest = (positionId, savedMax) => ({
  checks: [{ position_id: positionId, expected_max: toMax(savedMax) }]
})

// -> { outcome: 'stamped', check, max }
//  | { outcome: 'max_changed', current }
//  | { outcome: 'no_max' | 'not_found' | 'locked' | 'already_checked' | 'invalid' }
export const interpretStampResponse = (response, positionId) => {
  const results = response && Array.isArray(response.results) ? response.results : []
  const result = results.find(r => r && String(r.position_id) === String(positionId))
  if (!result) return { outcome: 'invalid' }
  if (result.status === STAMP_STATUS.stamped) {
    return { outcome: 'stamped', check: checkFromStampResult(result), max: result.max_seniority }
  }
  if (result.status === STAMP_STATUS.skipped) {
    if (result.reason === STAMP_REASONS.max_changed) return { outcome: 'max_changed', current: result.max_seniority }
    if (result.reason === STAMP_REASONS.no_max) return { outcome: 'no_max' }
    if (result.reason === STAMP_REASONS.not_found) return { outcome: 'not_found' }
    if (result.reason === STAMP_REASONS.locked) return { outcome: 'locked' }
    if (result.reason === STAMP_REASONS.already_checked) return { outcome: 'already_checked' }
  }
  return { outcome: 'invalid' }
}

export const stampOutcomeMessage = outcome => {
  if (outcome.outcome === 'max_changed') return TEXTS.maxChanged(outcome.current)
  if (outcome.outcome === 'no_max') return TEXTS.noMax
  if (outcome.outcome === 'not_found') return TEXTS.notFound
  if (outcome.outcome === 'locked') return TEXTS.locked
  return ''
}

// Hebrew toast text for a rejected request (axios error).
export const stampErrorMessage = error => {
  const response = error && error.response
  const status = response ? response.status : null
  const reason = response && response.data ? response.data.reason : null
  if (status === 401) return TEXTS.unauthorized
  if (status === 400 && BAD_REQUEST_REASONS.indexOf(reason) !== -1) return TEXTS.generic
  if (status === 400) return TEXTS.badRequest
  if (status === 503 || status === 404 || reason === 'not_migrated') return TEXTS.notReady
  return TEXTS.generic
}
